import { ThunkAction } from 'redux-thunk';
import * as moment from 'moment';
import { CasesTableFields, ICase, ICasesResponse, ILoadCasesRequest, ISearchFilter } from '@telstra-connect/channel-care/state/cases';
import { IChannelCareAppState } from '@telstra-connect/channel-care/state/index';
import {
    loadCasesSuccess,
    loadCasesError,
} from '../../actions/load-cases';
import { updateSupportFilter } from './update-support-filter';

interface IOtherSearchParams {
    original_cases: ICase[], fetchTime: number, selectedCompanyId: string, shouldUpdateSupportFilter: boolean
}

/**
 * This thunk action filters cases based on a search text and dispatches the result to the Redux store.
 * It retrieves the current state of cases and applies the search filter to return a list of matching cases.
 *
 * @param searchText - The text used for filtering cases. If not provided, all cases are returned.
 * @param otherParams - Additional parameters that may affect the filtering process.
 * - `original_cases`: The original list of cases before filtering.
 * - `fetchTime`: The timestamp indicating when the data was fetched.
 * - `selectedCompanyId`: The ID of the currently selected company for context in the filtering process.
 *
 * @Behavior
 * - It retrieves the current state of cases from the Redux store to ensure accurate filtering.
 * - The `getCasesMatchedtoSearchText` helper function is used to filter cases that match the search text,
 * checking all values within each case object for a match.
 * - If filtering is successful, it dispatches `loadCasesSuccess` with the filtered results and relevant parameters.
 * - In case of an error during filtering, it dispatches `loadCasesError` with an appropriate error message.
 */
export function filterCasesBySearch(searchText?: string, otherParams?: IOtherSearchParams):
ThunkAction<void, IChannelCareAppState, void> {
    return (dispatch, getState) => {
        try {
            const channelCareState: ILoadCasesRequest = getState()?.channelCare?.cases;
            if (channelCareState) {
                const existingSupportFilters: ISearchFilter = channelCareState.filter;
                const filteredCases = otherParams?.original_cases || channelCareState.data.original_cases;
                const checkedResult: ICasesResponse = {
                    cases: searchText ? getCasesMatchedtoSearchText(filteredCases, searchText) : filteredCases,
                    original_cases: filteredCases,
                    fetchTime: otherParams?.fetchTime || channelCareState.data.fetchTime
                };
                dispatch(loadCasesSuccess(checkedResult, otherParams?.selectedCompanyId || channelCareState.selectedCompanyId));
                dispatch(updateSupportFilter(filteredCases, !!otherParams?.shouldUpdateSupportFilter, existingSupportFilters));
            } else {
                dispatch(loadCasesError('channelCareState is null or undefined'));
            }
        } catch (error) {
            dispatch(loadCasesError('Error filtering data by search text'));
        }
    };
}

function getCasesMatchedtoSearchText(cases: ICase[], searchText: string): ICase[] {
    if (!cases?.length) {
        return []
    }
    return cases.filter((caseItem: ICase) =>
        Object.values(CasesTableFields).some((field: string) => {
            if (isValidDateString(caseItem[field])) {
                return convertDateString(caseItem[field])?.toLowerCase().includes(searchText.toLowerCase())
            }
            return caseItem[field]?.toLowerCase().includes(searchText.toLowerCase())
        })
    );
}

function isValidDateString(dateString) {
    return moment(dateString, moment.ISO_8601, true).isValid();
};

function convertDateString(dateString): string {
    const date = moment(dateString);
    if (!date.isValid()) {
        return dateString;
    }
    return date.format('DD MMM YY');
};
